/*
  Orbit hero (profondien.com のトップページ挙動を移植)
  ----------------------------------------------------
  ボールのデータはセクション内の <script type="application/json" data-orbit-data> から読む。
  カスタム要素なのでテーマエディタのセクション再描画にもそのまま追従する。
*/

class OrbitHero extends HTMLElement {
  static CONFIG = {
    ballWRatio: 0.8, // ボール直径: ビューポート幅に対する最大比率
    ballHRatio: 0.7, // ボール直径: ビューポート高さに対する最大比率
    gapRatio: 0.4, // ボール間の隙間（直径に対する比率）
    offsetXVmin: 0, // 中心からのX微調整（×vmin）
    centerYRatio: 0.5, // 軌道中心のY位置（0=上, 1=下）
    scrollSpeed: 0.04, // ホイールdelta→速度係数
    friction: 0.9, // 毎フレームの速度減衰
    snapLerp: 0.12, // スナップ時のlerp強度
    swipeThreshold: 40, // スワイプ判定の閾値(px)
  };

  connectedCallback() {
    const dataEl = this.querySelector("[data-orbit-data]");
    this.stage = this.querySelector("[data-orbit-stage]");
    if (!dataEl || !this.stage) return;

    this.data = JSON.parse(dataEl.textContent);
    this.count = this.data.length;
    this.step = 360 / this.count;
    this.deg = 0;
    this.velocity = 0;
    this.snapTarget = null;
    this.rafId = null;
    this.touchStartY = null;
    this.touchStartDeg = 0;
    this.scrolledSinceDown = false;
    this.abort = new AbortController();

    this.createBalls();
    this.bindEvents();
    this.measure();
    this.placeBalls(0);
  }

  disconnectedCallback() {
    this.abort.abort();
    if (this.rafId) cancelAnimationFrame(this.rafId);
  }

  createBalls() {
    this.balls = this.data.map((item) => {
      const { title, titleImage, body, image, href } = item;
      const el = document.createElement(href ? "a" : "div");
      el.className = "orbit-ball";
      if (href) {
        el.href = href;
        // 外部リンクのみ別タブで開く
        if (/^https?:\/\//.test(href) && !href.includes(location.host)) {
          el.target = "_blank";
          el.rel = "noopener";
        }
      }

      if (image) {
        el.style.backgroundImage = `url(${image})`;
        el.style.backgroundSize = "cover";
        el.style.backgroundPosition = "center";
      }

      const inner = document.createElement("div");
      inner.className = "orbit-ball-inner";

      if (titleImage) {
        const imgEl = document.createElement("img");
        imgEl.src = titleImage;
        imgEl.alt = "";
        imgEl.className = "orbit-ball-title-image";
        inner.appendChild(imgEl);
      } else if (title) {
        const titleEl = document.createElement("span");
        titleEl.className = "orbit-ball-title";
        titleEl.textContent = title;
        inner.appendChild(titleEl);
      }

      if (body) {
        const bodyEl = document.createElement("span");
        bodyEl.className = "orbit-ball-body";
        bodyEl.textContent = body;
        inner.appendChild(bodyEl);
      }

      el.appendChild(inner);

      if (href) {
        // スクロール操作の指離しで誤クリックしないようにする
        el.addEventListener("pointerdown", () => {
          this.scrolledSinceDown = false;
        });
        el.addEventListener("click", (e) => {
          if (this.scrolledSinceDown) e.preventDefault();
        });
      }

      this.stage.appendChild(el);
      return el;
    });
  }

  measure() {
    const C = OrbitHero.CONFIG;
    const W = window.innerWidth;
    const H = window.innerHeight;
    const vmin = Math.min(W, H);

    this.ballD = Math.min(W * C.ballWRatio, H * C.ballHRatio);
    this.orbitR =
      (this.ballD * (1 + C.gapRatio)) / (2 * Math.sin(Math.PI / this.count));
    this.orbitCX = W / 2 + this.orbitR + C.offsetXVmin * vmin;
    this.orbitCY = H * C.centerYRatio;

    this.balls.forEach((ball) => {
      ball.style.width = this.ballD + "px";
      ball.style.height = this.ballD + "px";
      const titleEl = ball.querySelector(".orbit-ball-title");
      const bodyEl = ball.querySelector(".orbit-ball-body");
      if (titleEl) titleEl.style.fontSize = this.ballD * 0.09 + "px";
      if (bodyEl) bodyEl.style.fontSize = this.ballD * 0.05 + "px";
    });
  }

  placeBalls(deg) {
    this.balls.forEach((ball, i) => {
      const baseDeg = this.step * i + 180;
      const totalRad = ((baseDeg + deg) * Math.PI) / 180;
      const bx = this.orbitCX + this.orbitR * Math.cos(totalRad) - this.ballD / 2;
      const by = this.orbitCY + this.orbitR * Math.sin(totalRad) - this.ballD / 2;
      ball.style.transform = `translate(${bx}px, ${by}px)`;
    });
  }

  tick = () => {
    const C = OrbitHero.CONFIG;
    if (this.snapTarget !== null) {
      this.deg += (this.snapTarget - this.deg) * C.snapLerp;
      this.placeBalls(this.deg);
      if (Math.abs(this.snapTarget - this.deg) < 0.05) {
        this.deg = this.snapTarget;
        this.snapTarget = null;
        this.placeBalls(this.deg);
        this.rafId = null;
      } else {
        this.rafId = requestAnimationFrame(this.tick);
      }
      return;
    }

    this.velocity *= C.friction;
    this.deg += this.velocity;
    this.placeBalls(this.deg);

    if (Math.abs(this.velocity) > 0.01) {
      this.rafId = requestAnimationFrame(this.tick);
    } else {
      this.velocity = 0;
      this.rafId = null;
    }
  };

  startTick() {
    if (!this.rafId) this.rafId = requestAnimationFrame(this.tick);
  }

  snapStep(dir) {
    this.snapTarget = Math.round(this.deg / this.step) * this.step + dir * this.step;
    this.velocity = 0;
    this.startTick();
  }

  bindEvents() {
    const C = OrbitHero.CONFIG;
    const opts = { signal: this.abort.signal };

    window.addEventListener(
      "wheel",
      (e) => {
        e.preventDefault();
        this.scrolledSinceDown = true;
        this.snapTarget = null;
        this.velocity += e.deltaY * C.scrollSpeed;
        this.startTick();
      },
      { passive: false, ...opts },
    );

    window.addEventListener(
      "touchstart",
      (e) => {
        this.touchStartY = e.touches[0].clientY;
        this.touchStartDeg = this.deg;
        this.velocity = 0;
        this.snapTarget = null;
      },
      { passive: true, ...opts },
    );

    window.addEventListener(
      "touchmove",
      (e) => {
        if (this.touchStartY === null) return;
        this.scrolledSinceDown = true;
        const moved = this.touchStartY - e.touches[0].clientY;
        this.deg = this.touchStartDeg + moved * ((this.step / window.innerHeight) * 2);
        this.placeBalls(this.deg);
      },
      { passive: true, ...opts },
    );

    window.addEventListener(
      "touchend",
      (e) => {
        if (this.touchStartY === null) return;
        const moved =
          this.touchStartY - (e.changedTouches[0]?.clientY ?? this.touchStartY);
        if (moved > C.swipeThreshold) {
          this.snapStep(1);
        } else if (moved < -C.swipeThreshold) {
          this.snapStep(-1);
        } else {
          this.snapTarget = Math.round(this.deg / this.step) * this.step;
          this.startTick();
        }
        this.touchStartY = null;
      },
      opts,
    );

    this.querySelector("[data-orbit-prev]")?.addEventListener(
      "click",
      () => this.snapStep(-1),
      opts,
    );
    this.querySelector("[data-orbit-next]")?.addEventListener(
      "click",
      () => this.snapStep(1),
      opts,
    );

    window.addEventListener(
      "resize",
      () => {
        this.measure();
        this.placeBalls(this.deg);
      },
      opts,
    );

    // iOSのバウンススクロール防止（リンク・ボタンのタップは妨げない）
    document.addEventListener(
      "touchmove",
      (e) => e.preventDefault(),
      { passive: false, ...opts },
    );
  }
}

if (!customElements.get("orbit-hero")) {
  customElements.define("orbit-hero", OrbitHero);
}
